import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";

import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  Stack,
  Avatar,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Divider,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  InputAdornment,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";

import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";

// Icons
import MapIcon from "@mui/icons-material/Map";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { getStatusColor, getStatusIcon } from "../utils/statusHelpers";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";

// Leaflet CSS
import "leaflet/dist/leaflet.css";

// Icon fix
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

// Custom marker colors based on status
const createCustomIcon = (status) => {
  const colors = {
    Pending: "#ffc107",
    Travelling: "#2196f3",
    "On Site": "#00bcd4",
    "In Progress": "#9c27b0",
  };

  const color = colors[status] || "#607d8b";

  const svgIcon = `
    <svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 0C6.716 0 0 6.716 0 15c0 8.284 15 25 15 25s15-16.716 15-25c0-8.284-6.716-15-15-15z" 
            fill="${color}" stroke="#fff" stroke-width="2"/>
      <circle cx="15" cy="15" r="5" fill="#fff"/>
    </svg>
  `;

  return L.divIcon({
    html: svgIcon,
    className: "custom-marker",
    iconSize: [30, 40],
    iconAnchor: [15, 40],
    popupAnchor: [0, -40],
  });
};

// Default icon fallback
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});
L.Marker.prototype.options.icon = DefaultIcon;

// --- MAIN COMPONENT ---
function LiveMap() {
  const [activeJobs, setActiveJobs] = useState([]);
  const [engineers, setEngineers] = useState(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedJob, setSelectedJob] = useState(null);

  // Listen for Active Jobs
  useEffect(() => {
    const activeJobsQuery = query(
      collection(db, "jobs"),
      where("status", "in", ["Pending", "Travelling", "On Site", "In Progress"])
    );

    const unsubscribe = onSnapshot(activeJobsQuery, (querySnapshot) => {
      const jobsList = [];
      querySnapshot.forEach((doc) => {
        jobsList.push({ id: doc.id, ...doc.data() });
      });
      setActiveJobs(jobsList);
    });

    return () => unsubscribe();
  }, []);

  // Fetch Engineer Details
  useEffect(() => {
    const fetchEngineerData = async () => {
      setIsLoading(true);
      const newEngineers = new Map(engineers);
      let needsUpdate = false;

      if (activeJobs.length > 0) {
        const engineerPromises = activeJobs.map(async (job) => {
          const engineerId = job.engineerId;
          if (engineerId && !newEngineers.has(engineerId)) {
            needsUpdate = true;
            try {
              const engineerDocRef = doc(db, "users", engineerId);
              const docSnap = await getDoc(engineerDocRef);
              if (docSnap.exists()) {
                const engineerData = docSnap.data();
                newEngineers.set(engineerId, {
                  ...engineerData,
                  id: engineerId,
                  name: `${engineerData.firstName || ""} ${
                    engineerData.lastName || ""
                  }`.trim(),
                  // TODO: replace placeholder with real live location data later
                  location: { lat: 53.745 + Math.random() * 0.1, lng: -0.332 + Math.random() * 0.1 }, // Hull area with slight variation
                });
              }
            } catch (err) {
              console.error("Error fetching engineer data:", err);
            }
          }
        });
        await Promise.all(engineerPromises);
      }

      if (needsUpdate) {
        setEngineers(newEngineers);
      }
      setIsLoading(false);
    };

    fetchEngineerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobs]);

  const mapCenter = [53.745, -0.332]; // Hull

  // Filtered jobs
  const filteredJobs = useMemo(() => {
    let filtered = [...activeJobs];

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((job) => job.status === statusFilter);
    }

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (job) =>
          job.customerName?.toLowerCase().includes(search) ||
          job.vehicleReg?.toLowerCase().includes(search) ||
          job.engineerName?.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [activeJobs, statusFilter, searchTerm]);

  // Statistics
  const stats = useMemo(() => {
    const statusCounts = {
      Pending: 0,
      Travelling: 0,
      "On Site": 0,
      "In Progress": 0,
    };

    activeJobs.forEach((job) => {
      if (statusCounts.hasOwnProperty(job.status)) {
        statusCounts[job.status]++;
      }
    });

    const engineerIds = new Set();
    activeJobs.forEach((job) => {
      if (job.engineerId) engineerIds.add(job.engineerId);
    });

    return {
      total: activeJobs.length,
      engineersOnDuty: engineerIds.size,
      ...statusCounts,
    };
  }, [activeJobs]);

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              bgcolor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              width: 56,
              height: 56,
            }}
          >
            <MapIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Live Engineer Map
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Real-time visualization of engineer locations and active jobs
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Statistics Dashboard */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Active Jobs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {stats.engineersOnDuty}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Engineers on Duty
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {stats.Travelling}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Travelling
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {stats["On Site"] + stats["In Progress"]}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                On Site / Working
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* LEFT: Job List */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              height: "70vh",
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Active Jobs ({filteredJobs.length})
              </Typography>

              {/* Search */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {/* Status Filter */}
              <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={(e, newFilter) => newFilter && setStatusFilter(newFilter)}
                size="small"
                fullWidth
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="Pending">Pending</ToggleButton>
                <ToggleButton value="Travelling">Travel</ToggleButton>
                <ToggleButton value="On Site">On Site</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Job List */}
            <Box sx={{ flex: 1, overflow: "auto" }}>
              {isLoading && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              {!isLoading && filteredJobs.length === 0 && (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <LocationOnIcon
                    sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    No Active Jobs
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {activeJobs.length === 0
                      ? "All jobs are complete or pending assignment"
                      : "No jobs match your filters"}
                  </Typography>
                </Box>
              )}

              {!isLoading && filteredJobs.length > 0 && (
                <List sx={{ p: 0 }}>
                  {filteredJobs.map((job) => {
                    const engineer = engineers.get(job.engineerId);
                    return (
                      <React.Fragment key={job.id}>
                        <ListItemButton
                          selected={selectedJob?.id === job.id}
                          onClick={() => setSelectedJob(job)}
                          sx={{
                            py: 2,
                            "&.Mui-selected": {
                              bgcolor: "rgba(102, 126, 234, 0.1)",
                              borderLeft: "4px solid #667eea",
                            },
                            "&:hover": {
                              bgcolor: "rgba(102, 126, 234, 0.05)",
                            },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: "#667eea" }}>
                              <PersonIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ mb: 0.5 }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {job.customerName || "Unknown Customer"}
                                </Typography>
                                <Chip
                                  icon={getStatusIcon(job.status)}
                                  label={job.status}
                                  size="small"
                                  color={getStatusColor(job.status)}
                                />
                              </Stack>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  🔧 {engineer?.name || "Loading engineer..."}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  🚗 {job.vehicleReg || "N/A"}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItemButton>
                        <Divider />
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>

            {/* Legend */}
            <Box
              sx={{
                p: 2,
                borderTop: 1,
                borderColor: "divider",
                bgcolor: "background.default",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, display: "block", mb: 1 }}
              >
                Map Legend
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      bgcolor: "#ffc107",
                      borderRadius: "50%",
                      border: "2px solid white",
                    }}
                  />
                  <Typography variant="caption">Pending</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      bgcolor: "#2196f3",
                      borderRadius: "50%",
                      border: "2px solid white",
                    }}
                  />
                  <Typography variant="caption">Travelling</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      bgcolor: "#00bcd4",
                      borderRadius: "50%",
                      border: "2px solid white",
                    }}
                  />
                  <Typography variant="caption">On Site</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      bgcolor: "#9c27b0",
                      borderRadius: "50%",
                      border: "2px solid white",
                    }}
                  />
                  <Typography variant="caption">In Progress</Typography>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        {/* RIGHT: Map */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={2}
            sx={{
              height: "70vh",
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  gap: 2,
                }}
              >
                <CircularProgress size={48} />
                <Typography>Loading map and engineer data…</Typography>
              </Box>
            ) : (
              <>
                {activeJobs.length === 0 && (
                  <Alert
                    severity="info"
                    sx={{
                      position: "absolute",
                      top: 16,
                      left: "50%",
                      transform: "translateX(-50%)",
                      zIndex: 1000,
                      maxWidth: "90%",
                    }}
                  >
                    No active jobs to display on the map
                  </Alert>
                )}
                <MapContainer
                  center={mapCenter}
                  zoom={12}
                  style={{ height: "100%", width: "100%" }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  {filteredJobs.map((job) => {
                    const engineer = engineers.get(job.engineerId);
                    if (engineer && engineer.location) {
                      return (
                        <Marker
                          key={engineer.id + "-" + job.id}
                          position={[engineer.location.lat, engineer.location.lng]}
                          icon={createCustomIcon(job.status)}
                        >
                          <Popup>
                            <Stack spacing={1} sx={{ minWidth: 200 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 700 }}
                              >
                                {engineer.name || engineer.email || "Engineer"}
                              </Typography>
                              <Divider />
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Status
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {job.status}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Customer
                                </Typography>
                                <Typography variant="body2">
                                  {job.customerName || "N/A"}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Vehicle
                                </Typography>
                                <Typography variant="body2">
                                  {job.vehicleReg || "N/A"}
                                </Typography>
                              </Box>
                              {job.siteAddress && (
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Site
                                  </Typography>
                                  <Typography variant="body2">
                                    {job.siteAddress}
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                          </Popup>
                        </Marker>
                      );
                    }
                    return null;
                  })}
                </MapContainer>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default LiveMap;